import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseClient';
import EnhancedTable from 'components/dashboard/EnhancedTable';
import ExpandableDetailRow from 'components/dashboard/ExpandableDetailRow';
import StatusBadge from 'components/dashboard/StatusBadge';
import ActionButtons from 'components/dashboard/ActionButtons';
import GlassyModal from 'components/ui/GlassyModal';
import AssignmentEditModal from 'components/modals/AssignmentEditModal';
import AdminAssignmentCreationForm from 'components/modals/AdminAssignmentCreationForm';
import EditableCell from 'components/dashboard/EditableCell';
import CommentSection from 'components/dashboard/CommentSection';
import ActivityFeed from 'components/dashboard/ActivityFeed';
import FloatingActionButton from 'components/ui/FloatingActionButton';
import Icon from 'components/AppIcon';
import { useAssignments } from 'hooks/useAssignments';
import { useProfiles } from 'hooks/useProfiles';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';

const EnhancedAssignmentsTable = () => {
  const { assignments, loading, updateStatus, deleteAssignment, updateAssignment } = useAssignments();
  const { profiles } = useProfiles();
  const { isAdmin, profile, user } = useAuth();
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const profilesMap = useMemo(() => {
    if (!profiles) return new Map();
    return new Map(profiles.map(p => [p.id, p]));
  }, [profiles]);

  // Helper function to get writer name from ID
  const getWriterName = (writerId) => {
    if (!writerId) return null;
    const writer = profilesMap.get(writerId);
    return writer ? writer.full_name : 'Unknown Writer';
  };

  // Helper function to get client name from ID
  const getClientName = (clientId) => {
    if (!clientId) return null;
    const client = profilesMap.get(clientId);
    return client ? client.full_name : 'Unknown Client';
  };

  // Enrich assignments with client names for mobile cards
  const enrichedAssignments = useMemo(() => {
    return assignments.map(assignment => ({
      ...assignment,
      client_name: getClientName(assignment.clientId)
    }));
  }, [assignments, profiles]);

  // Helper function to handle field updates
  const handleFieldUpdate = async (assignmentId, field, value) => {
    try {
      // Get old value for history tracking
      const assignment = assignments.find(a => a.id === assignmentId);
      const oldValue = assignment ? assignment[field] : null;
      
      // Clean and validate the value
      let cleanValue = value;
      
      // Handle empty strings
      if (cleanValue === '') {
        cleanValue = null;
      }
      
      // Handle specific field types
      if (field === 'pages') {
        cleanValue = parseInt(cleanValue) || 0;
      } else if (field === 'deadline') {
        cleanValue = cleanValue ? new Date(cleanValue) : null;
      }
      
      console.log('Updating assignment:', { assignmentId, field, value: cleanValue });
      await updateAssignment(assignmentId, { [field]: cleanValue });
      
      // Track edit history
      if (oldValue !== cleanValue) {
        try {
          console.log('Saving edit history:', {
            item_id: assignmentId,
            item_type: 'assignment',
            field: field,
            old_value: oldValue || '',
            new_value: cleanValue || '',
            editor_id: user?.uid,
            editor_name: profile?.full_name || user?.email,
            editor_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'user'
          });
          
          await addDoc(collection(db, 'edit_history'), {
            item_id: assignmentId,
            item_type: 'assignment',
            field: field,
            old_value: oldValue || '',
            new_value: cleanValue || '',
            editor_id: user?.uid,
            editor_name: profile?.full_name || user?.email,
            editor_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'user',
            created_at: serverTimestamp()
          });
          
          console.log('Edit history saved successfully');
        } catch (historyError) {
          console.error('Failed to save edit history:', historyError);
        }
      }
      
      toast.success('Assignment updated successfully');
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
      throw error;
    }
  };

  // Status options for dropdowns
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'revision-needed', label: 'Revision Needed' }
  ];

  // Dynamic status filter options based on actual data
  const statusFilterOptions = useMemo(() => {
    const uniqueStatuses = [...new Set(assignments.map(assignment => assignment.status).filter(Boolean))];
    const statusLabels = {
      'pending': 'Pending',
      'in-progress': 'In Progress', 
      'completed': 'Completed',
      'cancelled': 'Cancelled',
      'under-review': 'Under Review',
      'revision-needed': 'Revision Needed'
    };
    
    return uniqueStatuses.map(status => ({
      value: status,
      label: statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [assignments]);

  // Communication options
  const communicationOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'discord', label: 'Discord' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' }
  ];

  // Writer options
  const writerOptions = profiles
    .filter(p => p.is_writer)
    .map(writer => ({
      value: writer.id,
      label: `${writer.full_name} (${writer.email})`
    }));

  const columns = useMemo(
    () => [
      {
        accessorKey: 'assignment_title',
        header: 'Title',
        cell: ({ row }) => (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={16} className="text-primary" />
            </div>
            <div className="flex-1">
              <EditableCell
                value={row.original.assignment_title}
                onSave={(value) => handleFieldUpdate(row.original.id, 'assignment_title', value)}
                placeholder="Assignment title"
                isEditable={isAdmin()}
                className="font-medium"
                itemId={row.original.id}
                itemType="assignment"
                fieldName="assignment_title"
              />
              <EditableCell
                value={row.original.subject}
                onSave={(value) => handleFieldUpdate(row.original.id, 'subject', value)}
                placeholder="Subject"
                isEditable={isAdmin()}
                className="text-sm text-white/60"
              />
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Client',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-white">{row.original.name || 'Unknown'}</div>
            <div className="text-sm text-white/60">{row.original.email}</div>
          </div>
        ),
      },
      {
        accessorKey: 'assignment_type',
        header: 'Type',
        cell: ({ row }) => (
          <EditableCell
            value={row.original.assignment_type}
            onSave={(value) => handleFieldUpdate(row.original.id, 'assignment_type', value)}
            placeholder="Assignment type"
            isEditable={isAdmin()}
            className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm"
            itemId={row.original.id}
            itemType="assignment"
            fieldName="assignment_type"
          />
        ),
      },
      {
        accessorKey: 'pages',
        header: 'Pages',
        cell: ({ row }) => (
          <EditableCell
            value={row.original.pages}
            onSave={(value) => handleFieldUpdate(row.original.id, 'pages', parseInt(value) || 0)}
            placeholder="Pages"
            type="number"
            isEditable={isAdmin()}
            displayValue={`${row.original.pages} pages`}
            itemId={row.original.id}
            itemType="assignment"
            fieldName="pages"
          />
        ),
      },
      {
        accessorKey: 'deadline',
        header: 'Deadline',
        cell: ({ row }) => {
          const deadline = row.original.deadline;
          const isOverdue = deadline && new Date() > deadline;
          const deadlineValue = deadline ? deadline.toISOString().split('T')[0] : '';
          const displayValue = deadline ? format(deadline, 'MMM dd, yyyy') : 'No deadline';
          
          return (
            <div className={`${isOverdue ? 'text-red-400' : 'text-white'}`}>
              <EditableCell
                value={deadlineValue}
                onSave={(value) => handleFieldUpdate(row.original.id, 'deadline', value ? new Date(value) : null)}
                placeholder="No deadline"
                type="date"
                isEditable={isAdmin()}
                displayValue={displayValue}
                className={isOverdue ? 'text-red-400' : ''}
                itemId={row.original.id}
                itemType="assignment"
                fieldName="deadline"
              />
              {isOverdue && (
                <div className="text-xs text-red-400 flex items-center mt-1">
                  <Icon name="AlertCircle" size={12} className="mr-1" />
                  Overdue
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          isAdmin() ? (
            <EditableCell
              value={row.original.status}
              onSave={(value) => handleFieldUpdate(row.original.id, 'status', value)}
              type="select"
              options={statusOptions}
              isEditable={isAdmin()}
              displayValue={<StatusBadge status={row.original.status} />}
              itemId={row.original.id}
              itemType="assignment"
              fieldName="status"
            />
          ) : (
            <StatusBadge status={row.original.status} />
          )
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-white/80">
            {format(row.original.created_at, 'MMM dd, yyyy')}
          </span>
        ),
      },
    ],
    [isAdmin, handleFieldUpdate, statusOptions, communicationOptions, writerOptions]
  );

  const handleViewAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailsModal(true);
  };

  const handleEditAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setShowEditModal(true);
  };

  const handleDeleteAssignment = async (assignment) => {
    try {
      await deleteAssignment(assignment.id);
    } catch (error) {
      // Error already handled in the hook
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateStatus(id, status);
    } catch (error) {
      // Error already handled in the hook
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    // Assignments will automatically refresh due to real-time listeners
  };

  const AssignmentDetailsModal = () => (
    <GlassyModal
      isOpen={showDetailsModal}
      onClose={() => setShowDetailsModal(false)}
      title="Assignment Details"
      icon="FileText"
      maxWidth="max-w-3xl"
    >
      {selectedAssignment && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-white">
                {selectedAssignment.assignment_title}
              </h3>
              <p className="text-white/60">{selectedAssignment.subject}</p>
            </div>
            <div className="flex items-center space-x-3">
              <StatusBadge status={selectedAssignment.status} size="lg" />
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEditAssignment(selectedAssignment);
                }}
                className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 hover:text-green-300 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Icon name="Edit" size={16} />
                <span>Edit</span>
              </button>
            </div>
          </div>

          {/* Client Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">Client Information</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-white/60">Name:</span> {selectedAssignment.name}</p>
                <p><span className="text-white/60">Email:</span> {selectedAssignment.email}</p>
                <p><span className="text-white/60">Phone:</span> {selectedAssignment.phone_number}</p>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">Assignment Details</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-white/60">Status:</span> <StatusBadge status={selectedAssignment.status} /></p>
                <p><span className="text-white/60">Created:</span> {selectedAssignment.created_at && selectedAssignment.created_at instanceof Date ? format(selectedAssignment.created_at, 'MMM dd, yyyy') : 'Unknown'}</p>
                <p><span className="text-white/60">Last Updated:</span> {selectedAssignment.updated_at && selectedAssignment.updated_at instanceof Date ? format(selectedAssignment.updated_at, 'MMM dd, yyyy') : 'Not updated'}</p>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Requirements</h4>
            <EditableCell
              value={selectedAssignment.requirements || ''}
              onSave={(value) => handleFieldUpdate(selectedAssignment.id, 'requirements', value)}
              placeholder="No requirements specified"
              multiline={true}
              isEditable={isAdmin()}
              className="text-white/80 text-sm whitespace-pre-wrap"
              itemId={selectedAssignment.id}
              itemType="assignment"
              fieldName="requirements"
            />
          </div>

          {/* Additional Notes */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Additional Notes</h4>
            <EditableCell
              value={selectedAssignment.additional_notes || ''}
              onSave={(value) => handleFieldUpdate(selectedAssignment.id, 'additional_notes', value)}
              placeholder="No additional notes"
              multiline={true}
              isEditable={isAdmin()}
              className="text-white/80 text-sm whitespace-pre-wrap"
              itemId={selectedAssignment.id}
              itemType="assignment"
              fieldName="additional_notes"
            />
          </div>

          {/* Attachments */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-3 flex items-center space-x-2">
              <Icon name="Paperclip" size={18} className="text-white/80" />
              <span>Attachments</span>
              {selectedAssignment.attachments && selectedAssignment.attachments.length > 0 && (
                <span className="text-white/60">({selectedAssignment.attachments.length})</span>
              )}
            </h4>
            {selectedAssignment.attachments && selectedAssignment.attachments.length > 0 ? (
              <div className="space-y-3">
                {selectedAssignment.attachments.map((attachment, index) => (
                  <div key={index} className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                          <Icon name="FileText" size={16} className="text-primary" />
                        </div>
                        <div>
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white font-medium hover:text-primary transition-colors"
                          >
                            {attachment.name}
                          </a>
                          <div className="text-xs text-white/60 mt-1">
                            {attachment.type && <span className="mr-3">Type: {attachment.type}</span>}
                            {attachment.size && <span>Size: {(attachment.size / 1024).toFixed(1)} KB</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-primary/20 rounded-lg hover:bg-primary/30 transition-colors"
                          title="View file"
                        >
                          <Icon name="Eye" size={14} className="text-primary" />
                        </a>
                        <a
                          href={attachment.url}
                          download={attachment.name}
                          className="p-2 bg-green-500/20 rounded-lg hover:bg-green-500/30 transition-colors"
                          title="Download file"
                        >
                          <Icon name="Download" size={14} className="text-green-400" />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="FileX" size={48} className="text-white/20 mx-auto mb-2" />
                <p className="text-white/60">No attachments uploaded</p>
                <p className="text-white/40 text-sm">Files uploaded by the client will appear here</p>
              </div>
            )}
          </div>

          {/* QA Review Notes */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">QA Review Notes</h4>
            <EditableCell
              value={selectedAssignment.qa_review_notes || ''}
              onSave={(value) => handleFieldUpdate(selectedAssignment.id, 'qa_review_notes', value)}
              placeholder="No QA review notes"
              multiline={true}
              isEditable={isAdmin()}
              className="text-white/80 text-sm whitespace-pre-wrap"
              itemId={selectedAssignment.id}
              itemType="assignment"
              fieldName="qa_review_notes"
            />
          </div>

          {/* Communication */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Communication</h4>
            <div className="flex items-center space-x-2">
              <span className="text-white/60 text-sm">Preferred:</span>
              <EditableCell
                value={selectedAssignment.preferred_communication || ''}
                onSave={(value) => handleFieldUpdate(selectedAssignment.id, 'preferred_communication', value)}
                type="select"
                options={communicationOptions}
                isEditable={isAdmin()}
                placeholder="Select communication method"
                className="text-white/80 text-sm"
                itemId={selectedAssignment.id}
                itemType="assignment"
                fieldName="preferred_communication"
              />
            </div>
          </div>

          {/* Writer Assignment */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Assigned Writer</h4>
            <EditableCell
              value={selectedAssignment.assigned_writer || ''}
              onSave={(value) => handleFieldUpdate(selectedAssignment.id, 'assigned_writer', value)}
              type="select"
              options={writerOptions}
              isEditable={isAdmin()}
              placeholder="No writer assigned"
              displayValue={getWriterName(selectedAssignment.assigned_writer) || 'No writer assigned'}
              className="text-white/80 text-sm"
              itemId={selectedAssignment.id}
              itemType="assignment"
              fieldName="assigned_writer"
            />
          </div>

          {/* Comments and Edit History */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CommentSection 
              itemId={selectedAssignment.id}
              itemType="assignment"
            />
            <ActivityFeed 
              itemId={selectedAssignment.id}
              itemType="assignment"
            />
          </div>
        </div>
      )}
    </GlassyModal>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
            <Icon name="BookOpen" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Assignment Management</h2>
            <p className="text-text-muted text-sm">
              Manage and track all assignments
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="hidden md:flex bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg font-medium transition-colors items-center space-x-2"
        >
          <Icon name="Plus" size={16} />
          <span>Create New Assignment</span>
        </button>
      </div>

      {/* Outer transparent glass container */}
      <div className="bg-transparent backdrop-blur-none border-none rounded-xl p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: assignments.length, icon: 'FileText', color: 'blue' },
            { label: 'Pending', value: assignments.filter(a => a.status === 'pending').length, icon: 'Clock', color: 'yellow' },
            { label: 'In Progress', value: assignments.filter(a => a.status === 'in-progress').length, icon: 'Play', color: 'blue' },
            { label: 'Completed', value: assignments.filter(a => a.status === 'completed').length, icon: 'CheckCircle', color: 'green' },
          ].filter(stat => stat.label === 'Total' || stat.value > 0).map((stat) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card dashboard-card p-4 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">{stat.label}</p>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-500/20 rounded-lg flex items-center justify-center`}>
                  <Icon name={stat.icon} size={24} className={`text-${stat.color}-400`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Inner container for table section */}
        <div className="p-6 rounded-xl bg-transparent border-none shadow-none">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-white mb-2">Assignment Management</h2>
            <p className="text-white/60 text-sm">Manage and track all assignments in real-time</p>
          </div>
          
          <EnhancedTable
            data={enrichedAssignments}
            columns={columns}
            loading={loading}
            onRowClick={handleViewAssignment}
            searchPlaceholder="Search assignments..."
            emptyMessage="No assignments found"
            mobileCardLayout={true}
            mobileColumns={['assignment_title', 'assignment_type', 'deadline', 'client_name']}
            responsiveBreakpoint="lg"
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            statusFilterOptions={statusFilterOptions}
            useDropdownFiltersOnMobile={true}
            enableExpandableRows={true}
            expandableRowComponent={ExpandableDetailRow}
            expandableRowProps={{
              type: 'assignment',
              onView: handleViewAssignment,
              onEdit: handleEditAssignment,
              onDelete: handleDeleteAssignment,
              onStatusChange: handleStatusChange
            }}
            mobileActions={[
              {
                label: 'View Details',
                icon: 'Eye',
                onClick: handleViewAssignment,
                className: 'bg-primary/30 hover:bg-primary/40 text-white font-medium'
              },
              {
                label: 'Edit',
                icon: 'Edit',
                onClick: handleEditAssignment,
                className: 'bg-green-500/30 hover:bg-green-500/40 text-green-400'
              },
              {
                label: 'Delete',
                icon: 'Trash2',
                onClick: (assignment) => {
                  if (window.confirm('Are you sure you want to delete this assignment?')) {
                    handleDeleteAssignment(assignment);
                  }
                },
                className: 'bg-red-500/30 hover:bg-red-500/40 text-red-400'
              }
            ]}
          />
        </div>
      </div>

      {/* Details Modal */}
      <AssignmentDetailsModal />
      
      {/* Edit Modal */}
      <AssignmentEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        assignment={selectedAssignment}
        onSave={updateAssignment}
      />

      {/* Create Modal */}
      <AdminAssignmentCreationForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSuccess}
      />

      {/* Mobile FAB */}
      <FloatingActionButton
        onClick={() => setShowCreateModal(true)}
        icon="Plus"
        label="Create Assignment"
        position="bottom-right"
        size="md"
        showOnMobile={true}
        showOnDesktop={false}
      />
    </div>
  );
};

export default EnhancedAssignmentsTable;